import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpErrorResponse} from '@angular/common/http';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../auth-service/auth.service';
import {Router} from '@angular/router';
import {LocalStorageService} from 'ngx-webstorage';
import {FEATURE_FLAG, PERMISSION, ServiceProviderHub, SingleResponse} from '@generated-backend-types';
import {catchError, map, tap} from 'rxjs/operators';
import {STORAGE_KEYS} from '../../constants/storage.constants';
import {AUTH_ROUTES} from '../../constants/auth.constants';
import AuthenticatedContact = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Auth.AuthenticatedContact;
import {BehaviorSubject, filter, Observable, of, switchMap, take} from 'rxjs';

@Injectable({providedIn: 'root'})
export class UserSessionService {
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private authenticationService = inject(AuthService);
  private localStorageService = inject(LocalStorageService);

  private destroyRef = inject(DestroyRef);

  public loggedIn$ = new BehaviorSubject<boolean | undefined>(undefined);
  public user$ = new BehaviorSubject<AuthenticatedContact | undefined>(undefined);

  public login(email: string, password: string): Observable<SingleResponse<AuthenticatedContact>> {
    return this.authenticationService.login(email, password).pipe(
      take(1),
      tap({
        next: res => {
          this.handleLogin(res.data)
          this.localStorageService.store(STORAGE_KEYS.LOGIN_BROADCAST, Date.now())
        }
      })
    )
  }

  public handleLogin(user: AuthenticatedContact): void {
    this.localStorageService.store(STORAGE_KEYS.USER, {
      id: user.id,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      permissions: user.permissions,
      locale: user.locale,
      feature_flags: user.feature_flags,
    })
    this.user$.next(user)
    this.loggedIn$.next(true)
  }

  /** Resolves once `loggedIn$` is no longer `undefined`. Emits the active user, or `null` if not logged in. */
  public waitForSession(): Observable<AuthenticatedContact | null> {
    return this.loggedIn$.pipe(
      filter((l): l is boolean => typeof l === 'boolean'),
      take(1),
      switchMap(() => this.user$),
      take(1),
      map(user => user ?? null),
    );
  }

  public getStoredUserInfo(): Observable<AuthenticatedContact | undefined> {
    return of(this.localStorageService.retrieve(STORAGE_KEYS.USER) as AuthenticatedContact).pipe(
      map(user => {
        if (user) {
          return user
        } else {
          return undefined
        }
      })
    )
  }

  public logout(): Observable<void> {
    return this.authenticationService.logout().pipe(
      take(1),
      tap({
        next: () => {
          this.localStorageService.store(STORAGE_KEYS.LOGOUT_BROADCAST, Date.now())
          this.handleLogout()
        },
        error: () => {
          this.handleLogout()
        }
      })
    )
  }

  public hasAllPermission(permissions: PERMISSION[]): boolean {
    return this.checkPermissions(permissions, 'every');
  }

  public hasAnyPermission(permissions: PERMISSION[]): boolean {
    return this.checkPermissions(permissions, 'some');
  }

  public hasAllFeatureFlag(flags: FEATURE_FLAG[]): boolean {
    return this.checkFeatureFlags(flags, 'every');
  }

  public hasAnyFeatureFlag(flags: FEATURE_FLAG[]): boolean {
    return this.checkFeatureFlags(flags, 'some');
  }

  private checkPermissions(permissions: PERMISSION[], mode: 'every' | 'some'): boolean {
    const user = this.localStorageService.retrieve(STORAGE_KEYS.USER) as AuthenticatedContact;
    const userPermissions = user?.permissions;
    if (!userPermissions) return false;
    if (mode === 'every') return permissions.every(p => userPermissions.includes(p));
    return permissions.some(p => userPermissions.includes(p));
  }

  private checkFeatureFlags(flags: FEATURE_FLAG[], mode: 'every' | 'some'): boolean {
    const user = this.localStorageService.retrieve(STORAGE_KEYS.USER) as AuthenticatedContact;
    const userFlags = user?.feature_flags;
    if (!userFlags) return false;
    if (mode === 'every') return flags.every(f => userFlags[f] === true);
    return flags.some(f => userFlags[f] === true);
  }

  private handleLogout(): void {
    this.clearUserCache()
    this.user$.next(undefined)
    let notifyUser = false;
    if (this.loggedIn$.value === true) {
      notifyUser = true
    }
    this.loggedIn$.next(false)
    if (this.isOnAuthRoute()) {
      // /register, /login, /forgot-password, /password-reset are already the
      // right place for an unauthenticated user — don't yank them away.
      return;
    }
    this.router.navigate(['/login']).then(
      () => {
        if (notifyUser) {
          this.toastr.success('You have been logged out')
        }
      }
    )
  }

  private isLoggedIn(): Observable<AuthenticatedContact | null> {
    return this.authenticationService.getAuthenticatedUser().pipe(
      take(1),
      map(res => {
        if (res.data) {
          this.handleLogin(res.data)
        }
        return res.data
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            this.handleLogout()
            return of(null)
          }
        }
        throw err
      })
    )
  }

  private clearUserCache(): void {
    this.localStorageService.clear(STORAGE_KEYS.USER);
  }

  private isOnAuthRoute(): boolean {
    // Use window.location.pathname rather than router.url because this is
    // called from the service constructor — at DI time Angular's Router has
    // not yet navigated and router.url returns '/'. The browser's real path
    // is what we need.
    const url = this.getCurrentPath();
    return AUTH_ROUTES.some(route => url === route || url.startsWith(`${route}/`));
  }

  private getCurrentPath(): string {
    return window.location.pathname;
  }


  public constructor() {

    const onAuthRoute = this.isOnAuthRoute();

    if (onAuthRoute) {
      // On auth routes there is no network verification step, so the cache
      // alone cannot be trusted to drive `loggedIn$`. Resolve it as false
      // so guards can settle without acting on a stale cached session.
      this.loggedIn$.next(false);
    } else {
      this.getStoredUserInfo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: value => {
            if (value) {
              this.handleLogin(value);
            }
          }
        });

      this.isLoggedIn()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: value => {
            if (value === null || value === undefined) {
              this.clearUserCache()
            } else {
              this.user$.next(value)
            }
          }
        });
    }

    this.localStorageService.observe(STORAGE_KEYS.LOGOUT_BROADCAST)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleLogout()
        this.localStorageService.clear(STORAGE_KEYS.LOGOUT_BROADCAST)
      });

    this.localStorageService.observe(STORAGE_KEYS.LOGIN_BROADCAST).pipe(
      switchMap(() => this.isLoggedIn()),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
    // Cross-tab login pulls fresh /auth/user; handleLogin runs inside isLoggedIn().
    // Same-tab navigation after login is owned by LoginComponent.onSubmit.
  }
}
